/**
 * Collection pages — one continuous product list (loaded from layout/theme.liquid on the collection template only).
 * 2026-10-13: replaces the « Voir plus de produits » button. Pages are 50 products (paginate by 50).
 *
 * - Page 2 (the grid section's own render, ?section_id=…&page=2) is fetched as soon as the page is idle (network only).
 *   Page 2 is appended at the visitor's first scroll (2026-10-22: no longer for visitors who never scroll — half the
 *   DOM for them), later pages when the end of the loaded products is within ~2 screen heights (IntersectionObserver
 *   on the placeholder below them); each next page is prefetched right after an append.
 * - Reserved space: a placeholder <li class="bb-spacer"> after the products, as tall as the rows still to come (total
 *   from data-bb-items on the grid, row height and columns measured on the grid). An append fills the rows above it
 *   and shrinks it in the same frame, so the SEO block / footer below do not move (CLS). The estimate is ~2–4 % off,
 *   so when the end of the grid is already on screen or above it (slow connection: the visitor reached the footer
 *   before the next page arrived) the grid height is locked (never shorter) and, if it grows, the scroll position
 *   moves by the same amount in the same frame — what is on screen stays in place, the new products are added above.
 * - A failed fetch is retried after 2 s, 5 s and 10 s; after that a visible « Voir plus de produits » link to ?page=N
 *   replaces the placeholder.
 * - Back button: the number of loaded pages, the scroll position and the clicked product are kept in sessionStorage
 *   (on click and on pagehide); on a back/forward load those pages are appended right away and the clicked product is
 *   scrolled back to the same place (history.scrollRestoration = manual).
 * - Appended items are the server-rendered cards (sizes, reviews, "Aperçu rapide" via delegated clicks), never a
 *   product already in the grid; the page's <style data-bb-style> (order classes, main-collection-product-grid) goes
 *   before the grid, so banners keep their positions.
 * - Dawn's pagination stays in the HTML, visually hidden: real ?page=N links for Google and without JS.
 * - Filters / sort: Dawn's facets.js re-renders #ProductGridContainer — we restart on the new grid (a generation
 *   counter drops responses for the old one).
 */
(function () {
  'use strict';

  if (window.__bbAutoLoad) return;
  window.__bbAutoLoad = true;

  var RETRY_DELAYS = [2000, 5000, 10000];
  var KEY = 'bbAutoload:' + window.location.pathname;
  var state = null;
  var generation = 0;
  var observer = null;
  var firstScroll = null; // one-shot listener: page 2 on the first scroll
  var restoreData = null;
  var lastClick = null;

  try {
    if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
  } catch (e) {}

  function grid() {
    return document.getElementById('product-grid');
  }

  function pageFromUrl() {
    return parseInt(new URLSearchParams(window.location.search).get('page'), 10) || 1;
  }

  // Highest page number linked from Dawn's pagination (1 when there is none) — fallback for data-bb-pages.
  function totalPagesIn(root) {
    var max = 1;
    Array.prototype.forEach.call(root.querySelectorAll('.pagination a[href*="page="]'), function (a) {
      var m = a.getAttribute('href').match(/[?&]page=(\d+)/);
      if (m) max = Math.max(max, parseInt(m[1], 10));
    });
    return max;
  }

  function pageUrl(page, forSection) {
    var url = new URL(window.location.href);
    url.searchParams.set('page', page);
    var g = grid();
    if (forSection && g && g.dataset.id) url.searchParams.set('section_id', g.dataset.id);
    return url.toString();
  }

  function productKey(li) {
    var a = li.querySelector('a[href*="/products/"]');
    if (!a) return null;
    try {
      return new URL(a.getAttribute('href'), window.location.href).pathname;
    } catch (e) {
      return null;
    }
  }

  function products(g) {
    return g.querySelectorAll(':scope > li.grid__item');
  }

  function wait(ms) {
    return new Promise(function (resolve) { setTimeout(resolve, ms); });
  }

  function fetchOnce(page) {
    return fetch(pageUrl(page, true), { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var g = doc.getElementById('product-grid');
        if (!g) throw new Error('no #product-grid');
        return {
          items: Array.prototype.slice.call(g.children),
          styles: Array.prototype.slice.call(doc.querySelectorAll('style[data-bb-style]'))
        };
      });
  }

  // Fetch with 3 retries (2 s, 5 s, 10 s).
  function fetchPage(page, attempt) {
    attempt = attempt || 0;
    return fetchOnce(page).catch(function (err) {
      if (attempt >= RETRY_DELAYS.length) throw err;
      return wait(RETRY_DELAYS[attempt]).then(function () { return fetchPage(page, attempt + 1); });
    });
  }

  function idle(fn) {
    if ('requestIdleCallback' in window) window.requestIdleCallback(fn, { timeout: 1500 });
    else setTimeout(fn, 300);
  }

  // ── reserved space ──
  function measure(g) {
    var items = products(g);
    if (!items.length) return null;
    var top0 = items[0].offsetTop;
    var cols = 0;
    for (var i = 0; i < items.length && items[i].offsetTop === top0; i++) cols++;
    var gap = parseFloat(getComputedStyle(g).rowGap) || 0;
    // average height of the product rows already laid out (cards of a row share the row height)
    var rows = {};
    Array.prototype.forEach.call(items, function (li) {
      var t = li.offsetTop;
      rows[t] = Math.max(rows[t] || 0, li.offsetHeight);
    });
    var hs = Object.keys(rows).map(function (k) { return rows[k]; });
    var avg = hs.reduce(function (a, b) { return a + b; }, 0) / hs.length;
    return { cols: Math.max(cols, 1), rowH: avg + gap, count: items.length };
  }

  function updateSpacer() {
    var s = state;
    var g = grid();
    if (!s || !g) return;
    var m = measure(g);
    var remaining = m ? Math.max(s.items - m.count, 0) : 0;
    if (!m || remaining === 0 || s.next > s.total) {
      if (s.spacer) {
        s.spacer.remove();
        s.spacer = null;
      }
      return;
    }
    if (!s.spacer) {
      s.spacer = document.createElement('li');
      s.spacer.className = 'bb-spacer';
      s.spacer.setAttribute('aria-hidden', 'true');
      s.spacer.style.cssText = 'list-style:none;flex:0 0 100%;max-width:100%;width:100%;padding:0;margin:0;order:999999;pointer-events:none;';
      g.appendChild(s.spacer);
    }
    var h = Math.ceil(remaining / m.cols) * m.rowH - (parseFloat(getComputedStyle(g).rowGap) || 0);
    s.spacer.style.height = Math.max(Math.round(h), 0) + 'px';
  }

  // The end of the grid is on screen (or above it): its height is locked before products are added, so the page
  // never gets shorter there (Chrome counts the forced scroll change as a layout shift); unlocked once the end of the
  // grid is below the viewport again.
  function lockHeight(g) {
    g.style.minHeight = g.offsetHeight + 'px';
    if (g.__bbUnlock) return;
    g.__bbUnlock = function () {
      if (!g.isConnected || g.getBoundingClientRect().bottom > window.innerHeight) {
        g.style.minHeight = '';
        window.removeEventListener('scroll', g.__bbUnlock);
        g.__bbUnlock = null;
      }
    };
    window.addEventListener('scroll', g.__bbUnlock, { passive: true });
  }

  function nearEnd() {
    var s = state;
    if (!s || !s.spacer) return false;
    return s.spacer.getBoundingClientRect().top < window.innerHeight * 3;
  }

  // ── loading ──
  function prefetch() {
    var s = state;
    if (!s || s.pending || s.next > s.total) return;
    var gen = generation;
    var page = s.next;
    s.pending = fetchPage(page).then(
      function (data) { return gen === generation ? data : null; },
      function (err) {
        console.error('[Produits] Error loading page ' + page + ':', err);
        if (gen === generation) showMore(page);
        return null;
      }
    );
  }

  function appendNext() {
    var s = state;
    if (!s || s.appending || s.failed || s.next > s.total) return;
    if (!s.pending) prefetch();
    if (!s.pending) return;
    s.appending = true;
    var gen = generation;
    s.pending.then(function (data) {
      if (gen !== generation) return;
      s.appending = false;
      if (!data) return;
      var g = grid();
      if (!g) return;
      var seen = {};
      Array.prototype.forEach.call(products(g), function (li) {
        var k = productKey(li);
        if (k) seen[k] = true;
      });
      var endBefore = g.getBoundingClientRect().bottom;
      var endVisible = endBefore < window.innerHeight && !s.restoreTo;
      if (endVisible) lockHeight(g);
      data.styles.forEach(function (st) { g.parentNode.insertBefore(st, g); });
      var ref = s.spacer && s.spacer.parentNode === g ? s.spacer : null;
      data.items.forEach(function (item) {
        if (item.tagName !== 'LI' || item.classList.contains('bb-spacer')) return;
        if (item.classList.contains('grid__item')) {
          var k = productKey(item);
          if (k && seen[k]) return;
          if (k) seen[k] = true;
        }
        g.insertBefore(item, ref);
      });
      s.pending = null;
      s.next += 1;
      updateSpacer();
      if (endVisible) {
        var diff = g.getBoundingClientRect().bottom - endBefore;
        if (diff > 0) window.scrollBy(0, diff);
      }
      if (s.next > s.total) {
        if (observer) observer.disconnect();
        finishRestore();
        return;
      }
      prefetch();
      watchEnd();
      if (s.restoreTo && s.next <= s.restoreTo) appendNext();
      else {
        finishRestore();
        if (nearEnd()) requestAnimationFrame(appendNext);
      }
    });
  }

  // Later pages: when the placeholder below the products is within 2 screen heights.
  function watchEnd() {
    if (observer) observer.disconnect();
    var s = state;
    if (!s || !s.spacer || s.next > s.total) return;
    observer = new IntersectionObserver(
      function (entries) {
        if (entries.some(function (e) { return e.isIntersecting; })) appendNext();
      },
      { rootMargin: '0px 0px ' + Math.round(window.innerHeight * 2) + 'px 0px' }
    );
    observer.observe(s.spacer);
  }

  // All retries failed: a visible link to the next page instead of the placeholder.
  function showMore(page) {
    var s = state;
    var g = grid();
    if (!s || !g) return;
    s.failed = true;
    if (observer) observer.disconnect();
    if (s.spacer) {
      s.spacer.remove();
      s.spacer = null;
    }
    if (g.parentNode.querySelector('.bb-more')) return;
    var div = document.createElement('div');
    div.className = 'bb-more center';
    div.style.cssText = 'margin:3rem 0 1rem;text-align:center;';
    var a = document.createElement('a');
    a.className = 'button button--secondary';
    a.href = pageUrl(page, false);
    a.textContent = 'Voir plus de produits';
    div.appendChild(a);
    g.parentNode.insertBefore(div, g.nextSibling);
    finishRestore();
  }

  // ── back button ──
  function save(clicked) {
    var s = state;
    if (clicked) {
      var li = clicked.closest('li') || clicked;
      lastClick = { href: productKey(li), top: li.getBoundingClientRect().top };
    }
    var data = { search: window.location.search, pages: s && s.next ? s.next - 1 : 1, y: window.scrollY };
    if (lastClick) {
      data.href = lastClick.href;
      data.top = lastClick.top;
    }
    try {
      sessionStorage.setItem(KEY, JSON.stringify(data));
    } catch (e) {}
  }

  function readSaved() {
    try {
      var nav = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
      if (!nav || nav.type !== 'back_forward') return null;
      var d = JSON.parse(sessionStorage.getItem(KEY) || 'null');
      return d && d.search === window.location.search ? d : null;
    } catch (e) {
      return null;
    }
  }

  function finishRestore() {
    var s = state;
    if (!s || !s.restoreTo) return;
    s.restoreTo = 0;
    var d = restoreData;
    if (!d) return;
    var g = grid();
    var target = null;
    if (d.href && g) {
      Array.prototype.some.call(products(g), function (li) {
        if (productKey(li) === d.href) { target = li; return true; }
        return false;
      });
    }
    if (target) window.scrollTo(0, window.scrollY + target.getBoundingClientRect().top - (d.top || 0));
    else window.scrollTo(0, d.y || 0);
  }

  function setup() {
    var g = grid();
    if (observer) observer.disconnect();
    if (state && state.spacer) state.spacer.remove();
    generation++;
    state = null;
    if (!g || g.classList.contains('collection--empty') || !('IntersectionObserver' in window)) return;
    var container = document.getElementById('ProductGridContainer') || document;
    var page = parseInt(g.dataset.bbPage, 10) || pageFromUrl();
    var total = parseInt(g.dataset.bbPages, 10) || totalPagesIn(container);
    var size = parseInt(g.dataset.bbSize, 10) || 50;
    var items = parseInt(g.dataset.bbItems, 10) || total * size;
    g.dataset.bbAutoload = 'true';
    // items still to come after this page (the pages before it are not loaded here)
    state = {
      next: page + 1,
      total: total,
      items: Math.min(items, total * size) - (page - 1) * size,
      pending: null,
      appending: false,
      failed: false,
      spacer: null,
      restoreTo: 0
    };
    if (state.next > state.total) return;
    if (restoreData && restoreData.pages > page) {
      state.restoreTo = Math.min(restoreData.pages, total);
      updateSpacer();
      appendNext();
      return;
    }
    updateSpacer();
    watchEnd();
    // page 2: fetched when idle; appended (when idle) at the visitor's first scroll — still near the top, so the
    // rows fill in off-screen (no layout shift; appending near the end of the list shifted the cards on screen).
    // Visitors who never scroll keep half the DOM. watchEnd() still covers fast scrolling.
    idle(prefetch);
    if (firstScroll) window.removeEventListener('scroll', firstScroll);
    firstScroll = function () {
      window.removeEventListener('scroll', firstScroll);
      firstScroll = null;
      idle(appendNext);
    };
    window.addEventListener('scroll', firstScroll, { passive: true });
  }

  function init() {
    if (!grid()) return;
    restoreData = readSaved();
    setup();
    if (restoreData && state && !state.restoreTo) {
      // nothing to load again: just the scroll position
      requestAnimationFrame(function () {
        state.restoreTo = 1;
        finishRestore();
      });
    }
    // Filters / sort re-render the container: restart on the new grid.
    var container = document.getElementById('ProductGridContainer');
    if (container) {
      new MutationObserver(function () {
        var g = grid();
        if (g && !g.dataset.bbAutoload) {
          restoreData = null;
          setup();
        }
      }).observe(container, { childList: true });
    }
    document.addEventListener('click', function (e) {
      var a = e.target.closest && e.target.closest('#product-grid a[href*="/products/"]');
      if (a) save(a);
    }, true);
    window.addEventListener('pagehide', function () { save(null); });
    var rt;
    window.addEventListener('resize', function () {
      clearTimeout(rt);
      rt = setTimeout(function () {
        updateSpacer();
        watchEnd();
      }, 200);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
